/** Estado da interface (painéis, diálogos). */
import { create } from 'zustand';

/** Identificador de um painel registrado em `ui/panels.ts` ('satelite', 'queimadas', 'mapa', …). */
export type PanelTab = string;

/**
 * Folha do celular: `camadas` é a grade de interruptores (não-modal, mapa
 * livre); `half` e `full` são os ajustes da camada `tab` (modal), em duas
 * alturas.
 */
export type SheetStage = 'camadas' | 'half' | 'full';

const INSPECTOR_KEY = 'amaview:ultimos-focos-aberto';
const PANEL_KEY = 'amaview:painel';

/** Painel lateral (desktop): aberto/recolhido e aba ativa, lembrados entre visitas. */
function readPanel(): { open: boolean | null; tab: PanelTab | null } {
  try {
    const raw = globalThis.localStorage?.getItem(PANEL_KEY);
    if (!raw) return { open: null, tab: null };
    const v: unknown = JSON.parse(raw);
    if (!v || typeof v !== 'object') return { open: null, tab: null };
    const o = v as { open?: unknown; tab?: unknown };
    // "Próximos dias" foi aba do painel até 17/10/2026 (hoje tem entrada própria, à direita): volta ao padrão.
    const tab = typeof o.tab === 'string' && o.tab !== 'previsao' ? o.tab : null;
    return { open: typeof o.open === 'boolean' ? o.open : null, tab };
  } catch {
    return { open: null, tab: null };
  }
}

function writePanel(open: boolean, tab: PanelTab): void {
  try {
    globalThis.localStorage?.setItem(PANEL_KEY, JSON.stringify({ open, tab }));
  } catch {
    // navegação privada / armazenamento bloqueado: só não lembra
  }
}

function readInspector(): boolean {
  try {
    return globalThis.localStorage?.getItem(INSPECTOR_KEY) === '1';
  } catch {
    return false;
  }
}

function writeInspector(open: boolean): void {
  try {
    if (open) globalThis.localStorage?.setItem(INSPECTOR_KEY, '1');
    else globalThis.localStorage?.removeItem(INSPECTOR_KEY);
  } catch {
    // navegação privada / armazenamento bloqueado: só não lembra
  }
}

interface UiState {
  tab: PanelTab;
  /** Painel lateral aberto (desktop). */
  sidebarOpen: boolean;
  /**
   * Inspetor à direita aberto (desktop): "Últimos focos" e, no futuro,
   * detalhes de estação/sondagem. Flutua sobre o mapa, como o painel da
   * esquerda: os dois podem ficar abertos juntos (até 08/10/2026 eram colunas
   * da grade e se excluíam, para o mapa não ficar estreito demais).
   */
  inspectorOpen: boolean;
  /** Folha inferior aberta (celular). */
  sheetOpen: boolean;
  sheetStage: SheetStage;
  aboutOpen: boolean;
  exportOpen: boolean;
  /** Menu "Mais" (celular): compartilhar, exportar, sobre. */
  moreOpen: boolean;
  /** Opções do loop abertas (diálogo). */
  loopOptionsOpen: boolean;
  /** Diálogo do período personalizado aberto. */
  customPeriodOpen: boolean;
  online: boolean;
  /** Uma versão nova foi publicada e um pedaço do app não carregou (ver UpdateNotice). */
  updateAvailable: boolean;
  set: (patch: Partial<Omit<UiState, 'set' | 'openPanel' | 'togglePanel' | 'followPanel' | 'setInspector'>>) => void;
  /** Abre o painel numa aba. */
  openPanel: (tab: PanelTab) => void;
  /**
   * Ao ligar uma camada pelo trilho ou pelos chips: o painel, SE já estiver
   * aberto, passa a mostrar essa camada (opções e legenda à mão). Recolhido,
   * continua recolhido — ligar camadas olhando o mapa não pode mexer no layout.
   */
  followPanel: (tab: PanelTab) => void;
  /** Trilho: clicar na aba ativa recolhe o painel; noutra, troca. */
  togglePanel: (tab: PanelTab) => void;
  /** Abre/fecha o inspetor. */
  setInspector: (open: boolean) => void;
}

const inspectorInitially = readInspector();
const panelInitially = readPanel();

export const useUiStore = create<UiState>()((set, get) => ({
  tab: panelInitially.tab ?? 'satelite',
  // Na primeira visita o painel começa recolhido, como no celular (pedido do usuário, 09/10/2026):
  // o mapa inteiro à vista, com o trilho de camadas ao lado. Depois vale a última escolha (`writePanel`).
  sidebarOpen: panelInitially.open ?? false,
  inspectorOpen: inspectorInitially,
  sheetOpen: false,
  sheetStage: 'camadas',
  aboutOpen: false,
  exportOpen: false,
  moreOpen: false,
  loopOptionsOpen: false,
  customPeriodOpen: false,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  updateAvailable: false,
  set: (patch) => set(patch),
  openPanel: (tab) => set({ tab, sidebarOpen: true }),
  togglePanel: (tab) => {
    const s = get();
    if (s.sidebarOpen && s.tab === tab) set({ sidebarOpen: false });
    else s.openPanel(tab);
  },
  followPanel: (tab) => {
    if (get().sidebarOpen) set({ tab });
  },
  setInspector: (open) => {
    set({ inspectorOpen: open });
    writeInspector(open);
  },
}));

// Lembra painel aberto/recolhido e aba ativa a cada mudança.
useUiStore.subscribe((s, prev) => {
  if (s.sidebarOpen !== prev.sidebarOpen || s.tab !== prev.tab) writePanel(s.sidebarOpen, s.tab);
});
